import React, { useEffect, useState, useRef, useMemo } from 'react';
import { useCulturalEventsAPI } from '../hooks/useCulturalEventsAPI';
import { CulturalEventCard } from './CulturalEventCard';
// import { CulturalEventsFilters } from './CulturalEventsFilters'; // TODO: Implement filters
import { LoadingSpinner } from '../Shared/LoadingSpinner';
import { ErrorMessage } from '../Shared/ErrorMessage';
import { useTigerLilyAnimation } from '../hooks/useTigerLilyAnimation';
import { useBrownBlueSpeckleBackground } from '../hooks/useBrownBlueSpeckleBackground';
import { CulturalEvent } from '../constants/culturalEventsTypes';
import './CulturalEventsPage.css';

// Sort events by date (day-month) and time
const sortEventsByDate = (events: CulturalEvent[]): CulturalEvent[] => {
  if (!events || events.length === 0) return [];
  
  return [...events].sort((a, b) => {
    try {
      // Parse dates from strings
      const dateA = new Date(a.date);
      const dateB = new Date(b.date);
      
      // Check if dates are valid
      if (isNaN(dateA.getTime()) || isNaN(dateB.getTime())) {
        console.warn('Invalid date found in events:', { 
          a: { date: a.date, parsed: dateA }, 
          b: { date: b.date, parsed: dateB } 
        });
        return 0;
      }
      
      // Get month and day for comparison (ignore year - sort by day-month only)
      // This assumes all events are in the same year (2026) for display purposes
      const monthA = dateA.getMonth();
      const dayA = dateA.getDate();
      
      const monthB = dateB.getMonth();
      const dayB = dateB.getDate();
      
      // Compare month first
      if (monthA !== monthB) {
        return monthA - monthB;
      }
      
      // Then day (ignoring year)
      if (dayA !== dayB) {
        return dayA - dayB;
      }
      
      // If same date, sort by time
      const timeA = a.time || '00:00:00';
      const timeB = b.time || '00:00:00';
      return timeA.localeCompare(timeB);
    } catch (error) {
      console.error('Error sorting events:', error, { a: a.date, b: b.date });
      return 0;
    }
  });
};

export const CulturalEventsPage: React.FC = () => {
  const { events, loading, error, getPublicEvents } = useCulturalEventsAPI();
  const [filteredEvents, setFilteredEvents] = useState<CulturalEvent[]>([]);
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useTigerLilyAnimation(containerRef);
  const heroRef = useRef<HTMLElement | null>(null);
  const heroCanvasRef = useBrownBlueSpeckleBackground(heroRef);

  // Sort events by date
  const sortedEvents = useMemo(() => sortEventsByDate(events), [events]);

  useEffect(() => {
    getPublicEvents();
  }, [getPublicEvents]);

  useEffect(() => {
    setFilteredEvents(sortedEvents);
  }, [sortedEvents]);

  return (
    <div className="cultural-events-page">
      {/* Hero Section */}
      <section className="events-page-hero" ref={heroRef}>
        <canvas 
          ref={heroCanvasRef} 
          id="speckle-canvas-hero"
          className="speckle-canvas"
        />
        <div className="events-page-container">
          <h1 className="hero__title">Cultural Events</h1>
          <p className="hero__subtitle">
            Discover exclusive cultural experiences curated for Global Chic members
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="events-page-content" ref={containerRef}>
        <canvas 
          ref={canvasRef} 
          id="tiger-lily-canvas-events-page"
          className="tiger-lily-canvas"
        />
        <div className="events-page-container">
          {loading && (
            <div className="content__loading">
              <LoadingSpinner />
            </div>
          )}

          {error && (
            <div className="content__error">
              <ErrorMessage message={error} />
            </div>
          )}

          {!loading && !error && events.length === 0 && (
            <div className="content__empty">
              <h2>No events available</h2>
              <p>Check back soon for upcoming events!</p>
            </div>
          )}

          {!loading && !error && events.length > 0 && (
            <>
              {/* Filters - We'll implement this later */}
              {/* <CulturalEventsFilters 
                events={events} 
                onFilterChange={handleFilterChange}
              /> */}

              {/* Events Grid */}
              <div className="events-page-grid">
                {filteredEvents.map((event) => (
                  <CulturalEventCard key={event._id} event={event} />
                ))}
              </div>

              {/* Results Count */}
              <div className="events-page-footer">
                <p className="results-count">
                  Showing {filteredEvents.length} of {sortedEvents.length} events
                </p>
              </div>
            </>
          )}
        </div>
      </section>
    </div>
  );
};
